import { useState } from 'react';
import { Alert, Box, Collapse, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useMatchExternalRefEvents } from '../../api/useMatchExternalRefs';

interface Props {
  matchKey: string;
}

/**
 * Minimal polling panel for what Trade360 signals are actually arriving for
 * a live-tested match. Deliberately separate from — and much smaller than —
 * the existing (ENGAGE-fed) "Live match events" tab (`LiveMatchEventsTab`):
 * no WebSocket, no gateway, just a `GET .../events` poll while expanded.
 */
export function Trade360EventsPanel({ matchKey }: Props) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError } = useMatchExternalRefEvents(matchKey, {
    enabled: open
  });
  const events = data ?? [];

  return (
    <Box sx={{ mt: 1 }}>
      <Stack
        component="button"
        type="button"
        direction="row"
        alignItems="center"
        spacing={0.5}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          background: 'none',
          border: 'none',
          p: 0,
          color: 'inherit',
          font: 'inherit'
        }}
      >
        {open ? (
          <ExpandMoreIcon fontSize="small" />
        ) : (
          <ChevronRightIcon fontSize="small" />
        )}
        <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
          Trade360 live events
        </Typography>
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 3, pt: 0.5 }}>
          {isLoading && (
            <Typography sx={{ fontSize: '0.68rem', color: 'grey.500' }}>
              Loading…
            </Typography>
          )}
          {isError && (
            <Alert severity="warning" sx={{ py: 0, fontSize: '0.68rem' }}>
              Could not load Trade360 events.
            </Alert>
          )}
          {!isLoading && !isError && events.length === 0 && (
            <Typography sx={{ fontSize: '0.68rem', color: 'grey.500' }}>
              No events yet.
            </Typography>
          )}
          {events.length > 0 && (
            <Stack spacing={0.25}>
              {events.map((ev, i) => (
                <Box key={`${ev.observedAtMs}-${i}`}>
                  <Typography sx={{ fontSize: '0.7rem' }}>
                    {ev.signalType} · {ev.subject ?? '—'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'grey.500' }}>
                    magnitude {ev.magnitude ?? '—'} ·{' '}
                    {new Date(ev.observedAtMs).toLocaleTimeString()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default Trade360EventsPanel;
